import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, ImageSquare, CurrencyDollar, Flag, Bank, EyeSlash, Prohibit, ArrowClockwise, TrashSimple } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Stat = ({ label, value, testId }) => (
  <div className="bg-card border border-border p-5" data-testid={testId}>
    <p className="font-display text-3xl">{value}</p>
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
  </div>
);

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [reports, setReports] = useState([]);
  const [payments, setPayments] = useState([]);
  const [connect, setConnect] = useState([]);
  const [uQ, setUQ] = useState("");
  const [uRole, setURole] = useState("all");
  const [pQ, setPQ] = useState("");
  const [rStatus, setRStatus] = useState("open");

  const loadStats = useCallback(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  const loadUsers = useCallback(() => {
    const p = new URLSearchParams();
    if (uQ) p.set("q", uQ);
    if (uRole !== "all") p.set("role", uRole);
    api.get(`/admin/users?${p.toString()}`).then(({ data }) => setUsers(data)).catch(() => {});
  }, [uQ, uRole]);
  const loadPosts = useCallback(() => {
    const p = new URLSearchParams();
    if (pQ) p.set("q", pQ);
    api.get(`/admin/posts?${p.toString()}`).then(({ data }) => setPosts(data)).catch(() => {});
  }, [pQ]);
  const loadReports = useCallback(() => {
    api.get(`/admin/reports?status=${rStatus}`).then(({ data }) => setReports(data)).catch(() => {});
  }, [rStatus]);
  const loadPayments = useCallback(() => { api.get("/admin/payments").then(({ data }) => setPayments(data)).catch(() => {}); }, []);
  const loadConnect = useCallback(() => { api.get("/admin/creators/connect").then(({ data }) => setConnect(data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") { navigate("/"); return; }
    loadStats(); loadUsers(); loadPosts(); loadReports(); loadPayments(); loadConnect();
  }, [user, navigate, loadStats, loadUsers, loadPosts, loadReports, loadPayments, loadConnect]);

  const banToggle = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/${u.banned ? "unban" : "ban"}`);
      toast.success(u.banned ? `${u.username} unbanned` : `${u.username} banned`);
      loadUsers(); loadStats();
    } catch { toast.error("Action failed"); }
  };
  const setRole = async (u, role) => {
    try {
      await api.post(`/admin/users/${u.id}/role`, { role });
      toast.success(`${u.username} → ${role}`);
      loadUsers(); loadStats();
    } catch { toast.error("Action failed"); }
  };
  const hideToggle = async (p) => {
    try {
      await api.post(`/admin/posts/${p.id}/${p.hidden ? "unhide" : "hide"}`);
      toast.success(p.hidden ? "Post unhidden" : "Post hidden");
      loadPosts(); loadStats();
    } catch { toast.error("Action failed"); }
  };
  const deletePost = async (p) => {
    if (!window.confirm("Delete this post permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/posts/${p.id}`);
      toast.success("Post deleted");
      loadPosts(); loadStats();
    } catch { toast.error("Action failed"); }
  };
  const resolveReport = async (r) => {
    try {
      await api.post(`/admin/reports/${r.id}/resolve`);
      toast.success("Report resolved");
      loadReports(); loadStats();
    } catch { toast.error("Action failed"); }
  };

  if (!user || user.role !== "admin") {
    return <div className="py-32 text-center text-muted-foreground">Checking access…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-5 py-10" data-testid="admin-page">
      <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="font-display text-5xl md:text-6xl leading-none mb-8">Admin <span className="italic text-primary">console</span></motion.h1>

      <Tabs defaultValue="overview">
        <TabsList className="bg-card border border-border w-full justify-start mb-6 flex-wrap">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="content" data-testid="admin-tab-content">Content</TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-tab-reports">
            Reports {stats?.open_reports > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-primary text-primary-foreground">{stats.open_reports}</span>}
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="admin-tab-payouts">Payouts</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat testId="stat-users" label="Total users" value={stats?.users ?? 0} />
            <Stat testId="stat-creators" label="Creators" value={stats?.creators ?? 0} />
            <Stat testId="stat-fans" label="Fans" value={stats?.fans ?? 0} />
            <Stat testId="stat-banned" label="Banned" value={stats?.banned ?? 0} />
            <Stat testId="stat-posts" label="Posts" value={stats?.posts ?? 0} />
            <Stat testId="stat-hidden" label="Hidden posts" value={stats?.hidden_posts ?? 0} />
            <Stat testId="stat-subs" label="Active subs" value={stats?.active_subs ?? 0} />
            <Stat testId="stat-reports" label="Open reports" value={stats?.open_reports ?? 0} />
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="bg-card border border-border p-5">
              <div className="text-primary mb-2"><CurrencyDollar size={24} weight="fill" /></div>
              <p className="font-display text-3xl">${stats?.gmv ?? 0}</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">GMV (paid)</p>
            </div>
            <div className="bg-card border border-border p-5">
              <div className="text-primary mb-2"><Bank size={24} weight="fill" /></div>
              <p className="font-display text-3xl">${stats?.platform_revenue ?? 0}</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Platform revenue</p>
            </div>
            <div className="bg-card border border-border p-5">
              <div className="text-primary mb-2"><ImageSquare size={24} weight="fill" /></div>
              <p className="font-display text-3xl">{stats?.txn_count ?? 0}</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Transactions</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="flex flex-wrap gap-2 mb-4">
            <input data-testid="admin-users-search" value={uQ} onChange={(e) => setUQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadUsers()}
              placeholder="Search email, username, name…" className="flex-1 min-w-[240px] bg-card border border-border px-4 py-2.5 focus:border-primary outline-none" />
            <Select value={uRole} onValueChange={(v) => { setURole(v); }}>
              <SelectTrigger data-testid="admin-users-role" className="w-40 bg-card border-border"><SelectValue /></SelectTrigger>
              <SelectContent className="glass border-border">
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="fan">Fan</SelectItem>
                <SelectItem value="creator">Creator</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <button data-testid="admin-users-refresh" onClick={loadUsers} className="px-4 border border-border font-accent text-xs uppercase tracking-widest hover:border-foreground transition-colors">Search</button>
          </div>
          <div className="bg-card border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <tr><th className="p-3">User</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Payouts</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-${u.username}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center overflow-hidden">
                          {u.avatar_id ? <img src={fileUrl(u.avatar_id)} alt="" className="h-full w-full object-cover" /> : <span className="text-xs font-bold">{(u.display_name || "?")[0]}</span>}
                        </div>
                        <div>
                          <p className="font-medium">{u.display_name}</p>
                          <p className="text-[11px] text-muted-foreground">@{u.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-muted-foreground">{u.email}</td>
                    <td className="p-3">
                      <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                        <SelectTrigger data-testid={`admin-role-${u.username}`} className="h-8 w-28 bg-background border-border"><SelectValue /></SelectTrigger>
                        <SelectContent className="glass border-border">
                          <SelectItem value="fan">Fan</SelectItem>
                          <SelectItem value="creator">Creator</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3 text-[11px]">
                      {u.payouts_enabled ? <span className="text-primary">Active</span> : u.stripe_connect ? <span className="text-muted-foreground">Setup</span> : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-3">
                      {u.banned ? <span className="text-primary text-[11px] uppercase tracking-widest">Banned</span> : <span className="text-muted-foreground text-[11px]">Active</span>}
                    </td>
                    <td className="p-3 text-right">
                      <button data-testid={`admin-ban-${u.username}`} onClick={() => banToggle(u)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-border text-[11px] uppercase tracking-widest hover:border-primary hover:text-primary transition-colors">
                        <Prohibit size={12} /> {u.banned ? "Unban" : "Ban"}
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No users</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="content">
          <div className="flex gap-2 mb-4">
            <input data-testid="admin-posts-search" value={pQ} onChange={(e) => setPQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadPosts()}
              placeholder="Search caption or title…" className="flex-1 bg-card border border-border px-4 py-2.5 focus:border-primary outline-none" />
            <button onClick={loadPosts} className="px-4 border border-border font-accent text-xs uppercase tracking-widest hover:border-foreground">Search</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {posts.map((p) => (
              <div key={p.id} data-testid={`admin-post-${p.id}`} className="bg-card border border-border overflow-hidden">
                <div className="relative aspect-square bg-muted overflow-hidden">
                  {p.cover ? <img src={fileUrl(p.cover)} alt="" className="h-full w-full object-cover" /> : <div className="h-full flex items-center justify-center text-muted-foreground text-xs">no cover</div>}
                  {p.hidden && <span className="absolute top-2 left-2 glass border border-white/20 px-2 py-0.5 text-[10px] uppercase tracking-widest text-primary">Hidden</span>}
                  <span className="absolute bottom-2 right-2 glass border border-white/20 px-2 py-0.5 text-[10px] uppercase tracking-widest">{p.visibility}</span>
                </div>
                <div className="p-3">
                  <p className="text-xs text-muted-foreground truncate">@{p.creator?.username || "unknown"}</p>
                  <p className="text-sm truncate mb-2">{p.title || p.caption || "—"}</p>
                  <div className="flex gap-1.5">
                    <button data-testid={`admin-hide-${p.id}`} onClick={() => hideToggle(p)}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 border border-border text-[10px] uppercase tracking-widest hover:border-foreground">
                      <EyeSlash size={12} /> {p.hidden ? "Show" : "Hide"}
                    </button>
                    <button data-testid={`admin-delete-${p.id}`} onClick={() => deletePost(p)}
                      className="inline-flex items-center justify-center gap-1 px-2 py-1.5 border border-border text-[10px] uppercase tracking-widest hover:border-primary hover:text-primary">
                      <TrashSimple size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {posts.length === 0 && <p className="col-span-full py-16 text-center text-muted-foreground">No posts</p>}
          </div>
        </TabsContent>

        <TabsContent value="reports">
          <div className="flex gap-2 mb-4">
            <Select value={rStatus} onValueChange={setRStatus}>
              <SelectTrigger className="w-40 bg-card border-border"><SelectValue /></SelectTrigger>
              <SelectContent className="glass border-border">
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <button onClick={loadReports} className="px-4 border border-border font-accent text-xs uppercase tracking-widest hover:border-foreground inline-flex items-center gap-2"><ArrowClockwise size={14} /> Refresh</button>
          </div>
          <div className="flex flex-col divide-y divide-border bg-card border border-border">
            {reports.map((r) => (
              <div key={r.id} data-testid={`admin-report-${r.id}`} className="p-4 flex gap-4">
                <Flag size={22} weight="fill" className="text-primary shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    <span className="text-muted-foreground uppercase tracking-widest text-[10px]">{r.target_type}</span>{" "}
                    <span className="font-medium">
                      {r.target_type === "user" ? `@${r.target?.username || r.target_id}` : (r.target?.title || r.target?.caption || r.target_id)}
                    </span>
                  </p>
                  <p className="text-sm text-foreground/90 mt-1">"{r.reason}"</p>
                  <p className="text-[11px] text-muted-foreground mt-1">reported by @{r.reporter?.username || "?"} · {r.created_at?.slice(0, 10)}</p>
                </div>
                {r.status === "open" && (
                  <button data-testid={`admin-resolve-${r.id}`} onClick={() => resolveReport(r)}
                    className="px-4 py-2 border border-border text-[10px] uppercase tracking-widest hover:border-primary hover:text-primary shrink-0 h-fit">
                    Resolve
                  </button>
                )}
                {r.status === "resolved" && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Resolved</span>}
              </div>
            ))}
            {reports.length === 0 && <p className="p-8 text-center text-muted-foreground">No reports</p>}
          </div>
        </TabsContent>

        <TabsContent value="payments">
          <div className="bg-card border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <tr><th className="p-3">When</th><th className="p-3">Kind</th><th className="p-3">Buyer</th><th className="p-3">Creator</th><th className="p-3 text-right">Amount</th><th className="p-3 text-right">Platform fee</th><th className="p-3">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {payments.map((p) => (
                  <tr key={p.session_id} data-testid={`admin-tx-${p.session_id}`}>
                    <td className="p-3 text-muted-foreground text-[11px]">{(p.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3 uppercase text-[11px] tracking-widest">{p.kind}</td>
                    <td className="p-3">@{p.buyer || "?"}</td>
                    <td className="p-3">@{p.creator || "?"}</td>
                    <td className="p-3 text-right tabular-nums">${p.amount.toFixed(2)}</td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">${p.platform_fee.toFixed(2)}</td>
                    <td className="p-3">
                      <span className={`text-[11px] uppercase tracking-widest ${p.payment_status === "paid" ? "text-primary" : "text-muted-foreground"}`}>{p.payment_status}</span>
                    </td>
                  </tr>
                ))}
                {payments.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No transactions</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="payouts">
          <div className="bg-card border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <tr><th className="p-3">Creator</th><th className="p-3">Stripe Account</th><th className="p-3">Payouts</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {connect.map((c) => (
                  <tr key={c.id} data-testid={`admin-connect-${c.username}`}>
                    <td className="p-3">{c.display_name} <span className="text-muted-foreground text-[11px]">@{c.username}</span></td>
                    <td className="p-3 text-muted-foreground text-[11px]">{c.stripe_account_id || "—"}</td>
                    <td className="p-3">
                      {c.payouts_enabled ? <span className="text-primary text-[11px] uppercase tracking-widest">Active</span>
                        : c.stripe_account_id ? <span className="text-muted-foreground text-[11px]">Setup pending</span>
                        : <span className="text-muted-foreground text-[11px]">Not connected</span>}
                    </td>
                  </tr>
                ))}
                {connect.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-muted-foreground">No creators</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
